import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';

interface SentimentFilterContentProps {
  selectedSentiment: string;
  onSentimentChange: (sentiment: string) => void;
}

const SENTIMENT_EMOTIONS = [
  { id: 'feliz', emoji: '😀' },
  { id: 'animado', emoji: '🤩' },
  { id: 'tranquilo', emoji: '😌' },
  { id: 'cansado', emoji: '😴' },
  { id: 'frustrado', emoji: '😣' },
  { id: 'triste', emoji: '😭' },
  { id: 'estressado', emoji: '😤' },
  { id: 'irritado', emoji: '😠' },
  { id: 'ansioso', emoji: '😰' },
];

const VISIBLE_OPTIONS_COUNT = 5;

export default function SentimentFilterContent({ selectedSentiment, onSentimentChange }: SentimentFilterContentProps) {
  const { t } = useLanguage();
  
  const handleOptionPress = (sentimentId: string) => {
    onSentimentChange(sentimentId);
  };

  // Calculate height for exactly 5 options with 8px gaps
  const optionHeight = 60; // paddingVertical 20 + lineHeight 19.6 + borders
  const scrollViewHeight = (optionHeight * VISIBLE_OPTIONS_COUNT) + (8 * (VISIBLE_OPTIONS_COUNT - 1));

  return (
    <ScrollView 
      style={[styles.container, { height: scrollViewHeight }]} 
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {SENTIMENT_EMOTIONS.map((sentiment, index) => (
        <TouchableOpacity
          key={sentiment.id}
          style={[
            styles.optionCard,
            selectedSentiment === sentiment.id && styles.selectedOptionCard,
            index < SENTIMENT_EMOTIONS.length - 1 && styles.optionSpacing
          ]}
          onPress={() => handleOptionPress(sentiment.id)}
        >
          <Text style={styles.optionText}>
            {sentiment.emoji} {t(`diary.emotions.${sentiment.id}`)}
          </Text>
          <View style={[
            styles.checkbox,
            selectedSentiment === sentiment.id && styles.selectedCheckbox
          ]}>
            {selectedSentiment === sentiment.id && (
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path 
                  d="M12.6654 4.7915L6.2487 11.2082L3.33203 8.2915" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </Svg>
            )}
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  scrollContent: {
    paddingBottom: 8,
  },
  optionCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    gap: 4,
  },
  optionSpacing: {
    marginBottom: 8,
  },
  selectedOptionCard: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 19.6,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedCheckbox: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#015480',
    padding: 4,
  },
});